import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CloseIcon from "@mui/icons-material/Close";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { toast } from "react-toastify";
import { formatBytes, prepareImageUploadFile } from "../../utils/imageUpload";

const getDisplayFileName = (file, label) => {
  const name = String(file?.name || "").trim();
  if (!name) return "";
  if (/[�]/.test(name) || /\?{2,}/.test(name)) return `${label} 선택됨`;
  return name;
};

export function ImageUploadBox({
  label = "현장 사진",
  file,
  fileUrl = "",
  onFileChange,
  required = false,
  accept = "image/*",
  capture,
  disabled = false,
  compact = false,
  helperText = "큰 이미지는 업로드 전에 자동으로 압축됩니다.",
}) {
  const [previewUrl, setPreviewUrl] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const hasFile = Boolean(file);
  const canPreviewFile = typeof Blob !== "undefined" && file instanceof Blob;
  const hasStoredFile = Boolean(fileUrl);

  useEffect(() => {
    if (canPreviewFile) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(fileUrl || "");
    return undefined;
  }, [canPreviewFile, file, fileUrl]);

  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files?.[0] || null;
    event.target.value = "";
    if (!selectedFile) return;

    setIsProcessing(true);
    try {
      const result = await prepareImageUploadFile(selectedFile);
      onFileChange(result.file);
      if (result.compressed) {
        toast.info(
          `이미지를 ${formatBytes(result.originalSize)}에서 ${formatBytes(result.compressedSize)}로 최적화했습니다.`
        );
      }
    } catch (error) {
      toast.error(error.message || "이미지 처리 중 오류가 발생했습니다.");
    } finally {
      setIsProcessing(false);
    }
  };

  const displayName =
    getDisplayFileName(file, label) ||
    (hasStoredFile ? `${label} 등록됨` : `${label}${required ? " *" : ""}`);

  return (
    <Paper
      variant="outlined"
      sx={{
        overflow: "hidden",
        borderStyle: "dashed",
        borderColor: hasFile || hasStoredFile ? "primary.main" : "divider",
        borderRadius: 2,
        bgcolor: hasFile || hasStoredFile ? "#f8fbff" : "#f8fafc",
      }}
    >
      {previewUrl ? (
        <Box sx={{ position: "relative", bgcolor: "#e5e7eb" }}>
          <Box
            component="img"
            src={previewUrl}
            alt={`${label} 미리보기`}
            sx={{
              display: "block",
              width: "100%",
              height: compact ? 132 : 220,
              objectFit: "cover",
            }}
          />
          {hasFile ? (
            <IconButton
              size="small"
              onClick={() => onFileChange(null)}
              disabled={disabled || isProcessing}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                bgcolor: "rgba(15, 23, 42, 0.72)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(15, 23, 42, 0.86)" },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </Box>
      ) : (
        <Stack spacing={1} alignItems="center" justifyContent="center" sx={{ minHeight: compact ? 112 : 156, p: 2 }}>
          <CameraAltIcon color="disabled" sx={{ fontSize: compact ? 28 : 34 }} />
          <Typography variant="body2" sx={{ fontWeight: 800, color: "text.secondary" }}>
            {label}
          </Typography>
        </Stack>
      )}

      <Stack spacing={1} sx={{ p: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
          <Chip
            size="small"
            icon={<ImageOutlinedIcon />}
            label={displayName}
            sx={{
              minWidth: 0,
              maxWidth: "100%",
              "& .MuiChip-label": {
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                fontWeight: 800,
              },
            }}
          />
          {hasStoredFile ? (
            <Button
              size="small"
              href={fileUrl}
              target="_blank"
              rel="noreferrer"
              startIcon={<OpenInNewIcon />}
              sx={{ flexShrink: 0 }}
            >
              보기
            </Button>
          ) : null}
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
          <Button
            component="label"
            variant={hasFile || hasStoredFile ? "contained" : "outlined"}
            startIcon={isProcessing ? <CircularProgress color="inherit" size={16} /> : <UploadFileIcon />}
            disabled={disabled || isProcessing}
            sx={{ fontWeight: 900 }}
          >
            {isProcessing ? "이미지 처리 중" : hasFile || hasStoredFile ? "사진 변경" : "사진 첨부"}
            <input hidden type="file" accept={accept} capture={capture} onChange={handleFileSelect} />
          </Button>
          {helperText ? (
            <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
              {helperText}
            </Typography>
          ) : null}
        </Stack>
      </Stack>
    </Paper>
  );
}
